import { CREATOR_ACTION_TYPES } from './types';
import creatorService from '../services/creatorService';
// Action creators for creator management

/**
 * Fetch creator data action
 * @param {string} username - Creator username
 * @returns {Function} Redux thunk function
 */
export const fetchCreator = (username) => async (dispatch) => {
  dispatch({ type: CREATOR_ACTION_TYPES.FETCH_CREATOR_REQUEST });

  try {
    // Validate username
    if (!username || username.trim() === '') {
      const error = 'Username is required';
      dispatch({
        type: CREATOR_ACTION_TYPES.FETCH_CREATOR_FAILURE,
        payload: {
          error,
          status: 400
        }
      });
      return { success: false, error };
    }

    const response = await creatorService.fetchCreator(username.trim());

    if (response.success) {
      dispatch({
        type: CREATOR_ACTION_TYPES.FETCH_CREATOR_SUCCESS,
        payload: {
          creator: response.data,
          message: response.message
        }
      });

      return { success: true, data: response.data };
    } else {
      dispatch({
        type: CREATOR_ACTION_TYPES.FETCH_CREATOR_FAILURE,
        payload: {
          error: response.message || 'Failed to fetch creator data',
          status: response.status || 404
        }
      });

      return { success: false, error: response.message || 'Failed to fetch creator data' };
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
    const errorStatus = error.response?.status || error.status || 500;

    dispatch({
      type: CREATOR_ACTION_TYPES.FETCH_CREATOR_FAILURE,
      payload: {
        error: errorMessage,
        status: errorStatus
      }
    });

    return { success: false, error: errorMessage };
  }
};

/**
 * Fetch creator by username (alias for fetchCreator, with callback support)
 * @param {string} username - Creator username
 * @param {Function} callback - Optional callback function with (creatorId, creatorData) parameters
 * @returns {Function} Redux thunk function
 */
export const fetchCreatorByUsername = (username, callback) => async (dispatch) => {
  try {
    const result = await dispatch(fetchCreator(username));

    if (result.success && result.data) {
      // Extract creator ID from response data
      const creatorId = result.data?._id || result.data?.id || result.data?.user_data?._id || null;

      // Call callback if provided
      if (callback && typeof callback === 'function') {
        callback(creatorId, result.data);
      }
    }

    return result;
  } catch (error) {
    const errorMessage = error.message || 'Failed to fetch creator data';

    // Call callback with null values on error
    if (callback && typeof callback === 'function') {
      callback(null, null);
    }

    return { success: false, error: errorMessage };
  }
};

/**
 * Update creator profile action
 * @param {Object} profileData - Creator profile data to update
 * @returns {Promise<Object>} Update result
 */
export const updateCreator = async (profileData) => {
  try {
    const response = await creatorService.updateCreator(profileData);

    if (response.success) {
      return { success: true, data: response.data };
    } else {
      return { success: false, error: response.message };
    }
  } catch (error) {
    return { success: false, error: error.message || 'An unexpected error occurred' };
  }
};

/**
 * Update creator subscription data action
 * @param {Object} subscriptionData - Subscription data object with price, allow_tips, promotional_campaign
 * @returns {Promise<Object>} Update result
 */
export const updateSubscriptionData = async (subscriptionData) => {
  try {
    const response = await creatorService.updateSubscriptionData(subscriptionData);

    if (response.success) {
      return { success: true, data: response.data };
    } else {
      return { success: false, error: response.message };
    }
  } catch (error) {
    return { success: false, error: error.message || 'An unexpected error occurred' };
  }
};

/**
 * Delete creator account action
 * @param {string} email - Creator email
 * @param {Function} callback - Callback function to execute after deletion
 * @returns {Function} Redux thunk function
 */
export const deleteCreator = (email, callback) => async (dispatch) => {
  dispatch({ type: CREATOR_ACTION_TYPES.DELETE_CREATOR_REQUEST });

  try {
    const response = await creatorService.deleteCreator(email);

    if (response.success) {
      dispatch({
        type: CREATOR_ACTION_TYPES.DELETE_CREATOR_SUCCESS,
        payload: {
          message: response.message || 'Creator account deleted successfully'
        }
      });

      // Execute callback if provided
      if (callback && typeof callback === 'function') {
        callback();
      }

      return { success: true, data: response.data };
    } else {
      dispatch({
        type: CREATOR_ACTION_TYPES.DELETE_CREATOR_FAILURE,
        payload: {
          error: response.message || 'Failed to delete creator account',
          status: response.status
        }
      });

      return { success: false, error: response.message };
    }
  } catch (error) {
    dispatch({
      type: CREATOR_ACTION_TYPES.DELETE_CREATOR_FAILURE,
      payload: {
        error: error.message || 'An unexpected error occurred',
        status: error.status || 500
      }
    });

    return { success: false, error: error.message };
  }
};

/**
 * Clear creator errors action
 * @returns {Object} Action object
 */
export const clearCreatorErrors = () => ({
  type: CREATOR_ACTION_TYPES.CLEAR_CREATOR_ERRORS
});

/**
 * Set creator data action
 * @param {Object} creatorData - Creator data
 * @returns {Object} Action object
 */
export const setCreatorData = (creatorData) => ({
  type: CREATOR_ACTION_TYPES.SET_CREATOR_DATA,
  payload: { creator: creatorData }
});

/**
 * Post content action (single file or bundle)
 * @param {Object} requestBody - Content data to post
 * @returns {Promise<Object>} Post result
 */
export const postContent = async (requestBody) => {
  try {
    const response = await creatorService.postContent(requestBody);

    if (response.success) {
      return { success: true, data: response.data };
    } else {
      return { success: false, error: response.message };
    }
  } catch (error) {
    return { success: false, error: error.message || 'An unexpected error occurred' };
  }
};

/**
 * Fetch total tips for a creator (without dispatch)
 * @returns {Promise<Object>} Total tips result
 */
export const fetchTotalTip = async () => {
  try {
    const response = await creatorService.fetchTotalTip();

    if (response.success) {
      return { success: true, data: response.data };
    } else {
      return { success: false, error: response.message || 'Failed to fetch total tips' };
    }
  } catch (error) {
    return { success: false, error: error.message || 'An unexpected error occurred' };
  }
};

/**
 * Fetch creator audience threshold for mass messaging (without dispatch).
 * @returns {Promise<Object>} Audience threshold result
 */
export const getAudienceThreshold = async () => {
  try {
    const response = await creatorService.getAudienceThreshold();

    if (response.success) {
      return { success: true, data: response.data };
    }

    return { success: false, error: response.message || 'Failed to fetch audience threshold' };
  } catch (error) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      'An unexpected error occurred while fetching audience threshold';
    return { success: false, error: errorMessage };
  }
};

/**
 * Fetch featured creators for authenticated users
 * @param {Object} params - Query parameters object
 * @param {number} params.limit - Number of featured creators to fetch (default: 9)
 * @param {string} params.sortBy - Optional sort parameter
 * @param {number} params.skip - Optional skip parameter for pagination (default: 0)
 * @returns {Promise<{ success: boolean, data?: Object, error?: string }>} Featured creators result
 */
export const getCreatorLists = async (params = {}) => {
  try {
    const response = await creatorService.getCreatorLists(params);

    if (response.success) {
      return { success: true, data: response.data };
    }

    return {
      success: false,
      error: response.message || 'Failed to fetch featured creators',
    };
  } catch (error) {
    return {
      success: false,
      error: error?.message || 'Failed to fetch featured creators',
    };
  }
};

/**
 * Fetch featured creators for guest users
 * @param {Object} params - Query parameters object
 * @param {number} params.limit - Number of featured creators to fetch (default: 9)
 * @param {string} params.sortBy - Optional sort parameter (default: 'username_asc')
 * @returns {Promise<{ success: boolean, data?: Object, error?: string }>} Featured creators result
 */
export const getCreatorGuestLists = async (params = {}) => {
  try {
    const response = await creatorService.getCreatorGuestLists(params);

    if (response.success) {
      return { success: true, data: response.data };
    }

    return {
      success: false,
      error: response.message || 'Failed to fetch featured creators for guest users',
    };
  } catch (error) {
    return {
      success: false,
      error: error?.message || 'Failed to fetch featured creators for guest users',
    };
  }
};
